<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { UserIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useUserStore } from '~/store/UserStore'
import FlexibleDestination from '~/components/Navigation/FlexibleDestination.vue'

const route = useRoute()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const { t } = useI18n()

interface CurrentUserProps {
  disable: boolean
}

withDefaults(defineProps<CurrentUserProps>(), {
  disable: false,
})
const avatar = computed(() => {
  if (!(user && user.value && user.value.avatar)) {
    return null
  }
  return user.value.avatar.sizes
    ? user.value.avatar.sizes.s32
      ? user.value.avatar.sizes.s32
      : user.value.avatar.sizes.s64
        ? user.value.avatar.sizes.s64
        : user.value.avatar.original
    : user.value.avatar.original
})
async function logout() {
  const res = await userStore.logout()
  if (res.success) {
    return navigateTo('/login?logged-out=1')
  } else {
    // TODO handle error
    console.debug('error logging out', res.error)
  }
}
</script>

<template>
  <Popover class="relative">
    <PopoverButton
      :disabled="disable"
      :class="{ 'cursor-not-allowed': disable }"
      class="flex flex-col justify-center focus-visible:outline-0 text-navbar-contrast hover:opacity-80"
    >
      <UserIcon
        v-if="!(user && user.id)"
        class="h-6 w-6"
        :class="{ 'opacity-40': disable }"
        aria-hidden="true"
      />
      <div
        v-else-if="!avatar"
        class="h-6 w-6 inline-flex items-center justify-center border rounded-full border-navbar-contrast text-sm"
        style="line-height:0"
      >
        {{ user.displayName ? user.displayName.charAt(0).toUpperCase() : 'X' }}
      </div>
      <div
        v-else
        class="h-6 w-6 border rounded-full border-navbar-contrast inline-flex items-center justify-center"
      >
        <img
          :src="avatar"
          :alt="user.avatar?.altText ? user.avatar.altText : undefined"
          class="h-6 w-6 rounded-full"
        >
      </div>
    </PopoverButton>

    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <PopoverPanel class="absolute left-1/2 z-10 mt-2 flex w-screen max-w-min -translate-x-1/2 px-4">
        <div
          class="w-56 shrink rounded-md bg-tw-2 text-sm text-tw-1 shadow-lg border-tw-1 border"
        >
          <p
            v-if="user && user.id"
            class="truncate border-b border-tw-1 px-4 py-2"
            role="none"
          >
            <span
              class="block text-xs text-tw-2"
              role="none"
            >
              {{ t('SIGNED_IN_AS') }}
            </span>
            <span
              class="mt-0.5 font-semibold"
              role="none"
            >
              {{ user.email }}
            </span>
          </p>
          <ul class="space-y-2 py-3">
            <li v-if="user && user.id">
              <PopoverButton class="px-4">
                <router-link
                  to="/account"
                  class="dropdown-item"
                >
                  {{ t('ACCOUNT_SETTINGS') }}
                </router-link>
              </PopoverButton>
            </li>
            <li
              v-if="user && user.id"
              class="px-4"
            >
              <PopoverButton>
                <flexible-destination
                  path="/account/orders"
                  :type="route.name && route.path === '/account/orders' ? 'none' : 'internal'"
                  class="dropdown-item"
                >
                  {{ t('MY_ORDERS') }}
                </flexible-destination>
              </PopoverButton>
            </li>
            <li
              v-if="user && user.id"
              class="px-4"
            >
              <PopoverButton>
                <flexible-destination
                  path="/account/payment-methods"
                  :type="route.name && route.path === '/account/payment-methods' ? 'none' : 'internal'"
                  class="dropdown-item"
                >
                  {{ t('PAYMENT_METHODS') }}
                </flexible-destination>
              </PopoverButton>
            </li>
            <li
              v-if="user && user.id"
              class="px-4"
            >
              <PopoverButton>
                <flexible-destination
                  path="/account/addresses"
                  :type="route.name && route.path === '/account/addresses' ? 'none' : 'internal'"
                  class="dropdown-item"
                >
                  {{ t('ADDRESSES') }}
                </flexible-destination>
              </PopoverButton>
            </li>
            <li
              v-if="user && user.id"
              class="border-t border-tw-1 pt-3"
            >
              <PopoverButton class="px-4">
                <button
                  type="button"
                  class="dropdown-item"
                  @click="logout()"
                >
                  {{ t('LOG_OUT') }}
                </button>
              </PopoverButton>
            </li>
            <li v-else>
              <PopoverButton class="px-4">
                <router-link
                  class="dropdown-item"
                  to="/login"
                >
                  {{ t('LOGIN') }}
                </router-link>
              </PopoverButton>
            </li>
          </ul>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>
